import { createBrowserRouter, RouterProvider, Outlet, useLoaderData } from 'react-router-dom';
import { Planner1 } from './components_plans/Planner1';
import { Upcoming1 } from './components_upcoming/Upcoming1';
import { Community1 } from './components_community/Community1';
import { Map3Workspace } from './components_maps/stable/Map3Workspace';
import { AddMilestoneMap1 } from './components_maps/stable/AddMilestoneMap1';
import { Map3 } from './components_maps/stable/Map3';
import { BackupPage } from './utils_backup/BackupPage';
import SignIn from './components_auth/SignIn';
import SignOut from './components_auth/SignOut';
import SignUp from './components_auth/SignUp';
import { ComingSoon } from './pages/ComingSoon';
import { ConnectAgentPage } from './pages/ConnectAgentPage';
import { OAuthConnectPage } from './pages/OAuthConnectPage';
import { SupportPage } from './pages/SupportPage';
import { PrivateRoute } from './components_auth/PrivateRoute';
import { AdminSidebarLayout } from './components_admin/AdminSidebarLayout';
import { CompleteSignup } from './components_auth/CompleteSignup';
import { AccessDenied } from './pages/AccessDenied';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { AdminMilestoneMapsPage } from './components_admin/AdminMilestoneMapsPage';
import { AdminObjectivesPage } from './components_admin/AdminObjectivesPage';
import { UserProfileLoader } from './components_auth/UserProfileLoader';
import { MyMaps } from './pages/MyMaps';
import { SharedMapsPage } from './pages/SharedMapsPage';
import { AuthenticatedSidebarLayout } from './page_layouts/AuthenticatedSidebarLayout';
import { UserProfilePage } from './pages/UserProfilePage';
import { PendingPage } from './pages/PendingPage';
import { AdminSignupLinksPage } from './components_admin/AdminSignupLinksPage';
import { useEffect } from 'react';
import { HomePage } from './pages/HomePage';
import { MembershipPage } from './pages/MembershipPage';
import { ContactPage } from './pages/ContactPage';
import { UnauthenticatedStackedLayout } from './page_layouts/UnauthenticatedStackedLayout';
import { FeedbackPage } from './pages/FeedbackPage';
import { ChangelogPage } from './pages/ChangelogPage';
import { AdminUserProfilesPage } from './components_admin/AdminUserProfilesPage';
import { useSelector } from 'react-redux';
import { ThemesPage } from './pages/ThemesPage';
import { loadMapDataAsync } from './store/map3DataSlice';
import { startMapCollaboration } from './store/collaborationThunks';
import type { LoaderFunctionArgs, ShouldRevalidateFunction } from 'react-router-dom';
import { RootState } from './store/store';

// Add theme loader function
function themeLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const theme = url.searchParams.get('theme');
  return { theme };
}

/**
 * Detects the theme from the URL and applies the corresponding Tailwind theme to the page.
 *
 * ?theme=default
 * ?theme=colorblind
 *
 * Tailwind is set up to handle theme switching through the
 * data-theme attribute on the HTML element. When we set
 * document.documentElement.dataset.theme = theme in the ThemeProvider,
 * Tailwind picks this up and applies the appropriate theme styles. The
 * themes (default and colorblind) were previously defined in
 * tailwind.config.js when we were working on the theme implementation
 * earlier.
 *
 * @param param0
 * @returns
 */
function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useLoaderData() as { theme: string | null };

  useEffect(() => {
    if (theme) {
      // Store current title
      const currentTitle = document.title;
      document.documentElement.dataset.theme = theme;
      // Restore title after theme change
      document.title = currentTitle;
    } else {
      delete document.documentElement.dataset.theme;
    }
  }, [theme]);

  return <>{children}</>;
}

/**
 * Loader for base map view
 */
async function mapBaseLoader({ params }: LoaderFunctionArgs) {
  const { mapShortId } = params;
  if (!mapShortId) {
    throw new Response('Missing mapShortId', { status: 400 });
  }
  await store.dispatch(loadMapDataAsync(mapShortId));
  const state = store.getState();
  const milestoneMap = state.map3Data.milestoneMaps.find((m) => m.shortId === mapShortId);
  if (!milestoneMap) {
    throw new Response('Map not found', { status: 404 });
  }

  // Start real-time collaboration listeners for this map (with auto-cleanup)
  const objectiveUuids = state.map3Data.objectives.map((obj) => obj.uuid);
  console.log('[Routes] Starting collaboration for map:', milestoneMap.uuid);
  store.dispatch(
    startMapCollaboration({
      mapUuid: milestoneMap.uuid,
      objectiveUuids: milestoneMap.objectiveUuids,
    }),
  );

  return null;
}

/**
 * Prevent the map base loader from re-running when navigating within the same map
 * (e.g., clicking status cells to open/switch the drawer).
 *
 * The loader should only re-run when mapShortId changes — i.e., navigating to a
 * different map. Without this guard, every drawer navigation dispatches
 * loadMapDataAsync which sets isLoading=true and causes the map to flash a
 * loading state.
 */
const mapBaseShouldRevalidate: ShouldRevalidateFunction = ({ currentParams, nextParams }) => {
  return currentParams.mapShortId !== nextParams.mapShortId;
};

export const routes = [
  {
    element: (
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    ),
    loader: themeLoader,
    children: [
      // Unauthenticated routes
      {
        element: <UnauthenticatedStackedLayout />,
        children: [
          {
            path: '/',
            element: <HomePage />,
          },
          {
            path: '/membership',
            element: <MembershipPage />,
          },
          {
            path: '/contact',
            element: <ContactPage />,
          },
          {
            path: '/sign-in',
            element: <SignIn />,
          },
          {
            path: '/sign-up',
            element: <SignUp />,
          },
          {
            path: '/sign-out',
            element: <SignOut />,
          },
          {
            // OAuth login bridge (mm-mcp). Outside UserProfileLoader: it must work before a profile exists in Redux.
            path: '/oauth/connect',
            // react-router matches case-insensitively by default; the anti-framing headers do not (clickjacking, B8).
            caseSensitive: true,
            element: <OAuthConnectPage />,
          },
          {
            path: '/themes',
            element: <ThemesPage />,
          },
        ],
      },
      // Authenticated routes
      {
        element: (
          <UserProfileLoader>
            <Outlet />
          </UserProfileLoader>
        ),
        children: [
          {
            element: <AuthenticatedSidebarLayout />,
            children: [
              {
                path: ':userName',
                element: <UserProfilePage />,
              },
              {
                path: ':userName/notifications',
                element: <ComingSoon />,
              },
              {
                path: ':userName/settings',
                element: <ComingSoon />,
              },
              {
                path: ':userName/settings/agents',
                element: <ConnectAgentPage />,
              },
              {
                path: ':userName/maps',
                element: <MyMaps />,
              },
              {
                path: ':userName/bookmarks',
                element: <ComingSoon />,
              },
              {
                path: ':userName/shared',
                element: <SharedMapsPage />,
              },
              {
                path: ':userName/maps/:mapShortId',
                element: <Map3Workspace />,
                loader: mapBaseLoader,
                shouldRevalidate: mapBaseShouldRevalidate,
                children: [
                  {
                    index: true,
                    element: <Map3 />,
                  },
                ],
              },
              {
                path: ':userName/maps/add',
                element: <AddMilestoneMap1 />,
              },
              {
                path: '/plans',
                // TODO: Move to backlog & replace with "coming soon"
                element: <Planner1 />,
              },
              {
                path: '/upcoming',
                // TODO: Move to backlog & replace with "coming soon"
                element: <Upcoming1 />,
              },
              {
                path: '/community',
                // TODO: Move to backlog & replace with "coming soon"
                element: <Community1 />,
              },
              {
                path: '/support',
                element: <SupportPage />,
              },
              {
                path: '/support2',
                // TODO: Move to backlog & replace with "coming soon"
                element: <ComingSoon />,
              },
              {
                path: '/feedback',
                element: <FeedbackPage />,
              },
              {
                path: '/privacy-policy',
                element: <ComingSoon />,
              },
              {
                path: '/changelog',
                element: <ChangelogPage />,
              },
            ],
          },
          {
            path: '/admin',
            element: <AdminSidebarLayout />,
            children: [
              {
                path: 'user-profiles',
                element: <AdminUserProfilesPage />,
              },
              {
                path: 'milestone-maps',
                element: <AdminMilestoneMapsPage />,
              },
              {
                path: 'objectives',
                element: <AdminObjectivesPage />,
              },
              {
                path: 'signup-links',
                element: <AdminSignupLinksPage />,
              },
              {
                path: 'orgs',
                element: <ComingSoon />,
              },
              {
                path: 'backup',
                element: <BackupPage />,
              },
            ],
          },
          {
            path: '/complete-signup',
            element: (
              <PrivateRoute>
                <CompleteSignup />
              </PrivateRoute>
            ),
          },
          {
            path: '/access-denied',
            element: <AccessDenied />,
          },
          {
            path: '/403',
            element: <AccessDenied />,
          },
          {
            path: '/pending',
            element: <PendingPage />,
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export function Routes() {
  return (
    <Provider store={store}>
      <RouterProvider router={router} />
    </Provider>
  );
}
